import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, RotateCcw, Truck, Lock, Check, ArrowRight } from "lucide-react";

/**
 * Informational-only payment reassurance for the product page. Contains NO
 * payment-processing functionality — icons/links are informational. All
 * methods are selected dynamically by Stripe-hosted Checkout at eligibility.
 */
export const PaymentTrust = ({ className = "" }) => (
  <div className={`border border-white/10 rounded-md p-5 mb-6 ${className}`} data-testid="pdp-payment-trust">
    <div className="flex items-center gap-2 mb-2">
      <ShieldCheck size={15} className="text-emerald-500 shrink-0" aria-hidden="true" />
      <span className="text-sm font-medium text-zinc-200">Flexible &amp; Secure Payment Options</span>
    </div>
    <p className="text-xs text-zinc-500 leading-relaxed">
      Pay securely with major credit/debit cards, Apple Pay, Google Pay, US bank account (ACH), and other eligible payment methods.
    </p>
    <p className="text-[11px] text-zinc-600 leading-relaxed mt-1.5">
      Payment options are shown at checkout based on eligibility.
    </p>
    <Link
      to="/payment-options"
      data-testid="pdp-payment-options-link"
      className="inline-flex items-center gap-1 text-[11px] text-zinc-400 hover:text-zinc-200 underline underline-offset-2 mt-3"
    >
      Learn more about payment options <ArrowRight size={11} aria-hidden="true" />
    </Link>
  </div>
);

/** Compact one-line payment-options note for the cart (informational only). */
export const PaymentOptionsNote = ({ className = "" }) => (
  <div className={`text-[11px] text-zinc-500 leading-relaxed ${className}`} data-testid="cart-payment-options-note">
    <span>Cards · Apple Pay · Google Pay · US Bank Account (ACH) · More eligible options</span>{" "}
    <Link
      to="/payment-options"
      data-testid="cart-payment-options-link"
      className="text-zinc-400 hover:text-zinc-200 underline underline-offset-2 whitespace-nowrap"
    >
      View payment options
    </Link>
  </div>
);

/** Accepted payment method icons + secure-checkout note (FontAwesome brands). */
export const PaymentMethods = ({ className = "" }) => (
  <div className={`flex flex-col gap-2 ${className}`} data-testid="payment-methods">
    <div className="flex items-center gap-2 text-zinc-400">
      <i className="fa-brands fa-cc-visa text-2xl" title="Visa" aria-label="Visa" />
      <i className="fa-brands fa-cc-mastercard text-2xl" title="Mastercard" aria-label="Mastercard" />
      <i className="fa-brands fa-cc-amex text-2xl" title="American Express" aria-label="American Express" />
      <i className="fa-brands fa-cc-discover text-2xl" title="Discover" aria-label="Discover" />
      <span className="mx-1 h-4 w-px bg-white/15" />
      <i className="fa-brands fa-stripe text-2xl" title="Stripe" aria-label="Stripe" />
    </div>
    <div className="flex items-center gap-1.5 text-[11px] text-zinc-500">
      <Lock size={12} className="text-emerald-500" /> Secure checkout · payments processed by Stripe
    </div>
  </div>
);

/** Compact inline row of policy links for cart/checkout. */
export const PolicyLinksRow = ({ className = "" }) => (
  <div className={`flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-zinc-500 ${className}`} data-testid="checkout-policy-links">
    <Link to="/return-policy" className="hover:text-zinc-300 underline underline-offset-2" data-testid="checkout-link-return">Return Policy</Link>
    <span className="text-zinc-700">·</span>
    <Link to="/shipping-policy" className="hover:text-zinc-300 underline underline-offset-2" data-testid="checkout-link-shipping">Shipping Policy</Link>
    <span className="text-zinc-700">·</span>
    <Link to="/refund-policy" className="hover:text-zinc-300 underline underline-offset-2" data-testid="checkout-link-refund">Refund Policy</Link>
    <span className="text-zinc-700">·</span>
    <Link to="/privacy-policy" className="hover:text-zinc-300 underline underline-offset-2" data-testid="checkout-link-privacy">Privacy Policy</Link>
    <span className="text-zinc-700">·</span>
    <Link to="/terms-conditions" className="hover:text-zinc-300 underline underline-offset-2" data-testid="checkout-link-terms">Terms &amp; Conditions</Link>
  </div>
);

/** Compact Shipping & Returns trust card for the product detail page. */
export const ShippingReturnsCard = () => {
  const points = [
    { icon: RotateCcw, text: "30-Day Returns" },
    { icon: Truck, text: "Return shipping paid by Lecis Auto Glass on approved returns" },
    { icon: ShieldCheck, text: "Secure Checkout" },
    { icon: Truck, text: "Nationwide Shipping" },
  ];
  return (
    <div className="border border-white/10 rounded-md p-5 mb-6" data-testid="pdp-shipping-returns">
      <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold mb-4">Shipping &amp; Returns</div>
      <ul className="space-y-2.5 mb-4">
        {points.map((p, i) => (
          <li key={i} className="flex items-start gap-2.5 text-sm text-zinc-200">
            <Check size={15} className="text-emerald-500 mt-0.5 shrink-0" />
            <span>{p.text}</span>
          </li>
        ))}
      </ul>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-zinc-500 pt-3 border-t border-white/5">
        <Link to="/return-policy" className="hover:text-zinc-300 underline underline-offset-2" data-testid="pdp-link-return">Return Policy</Link>
        <span className="text-zinc-700">·</span>
        <Link to="/shipping-policy" className="hover:text-zinc-300 underline underline-offset-2" data-testid="pdp-link-shipping">Shipping Policy</Link>
        <span className="text-zinc-700">·</span>
        <Link to="/refund-policy" className="hover:text-zinc-300 underline underline-offset-2" data-testid="pdp-link-refund">Refund Policy</Link>
      </div>
    </div>
  );
};
